import { forwardRef, useState } from 'react';

import styled from 'styled-components';

interface WeInputProps {
  fontSize: string;
  padding: string;
  name?: string;
  value?: string;
  placeholder?: string;
  border?: string;
  borderRadius?: string;
  color?: string;
  focusColor?: string;
  defaultValue?: string;
  disabled?: boolean;
  minWidth?: string;
  maxWidth?: string;
  iconAlign?: 'left' | 'right';
  textAlign?: 'left' | 'right' | 'center';
  backgroundColor?: string;
  background?: string;
  height?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  focusBorder?: string;
}

const InputRoot = styled.div<{
  $height?: string;
  $iconAlign?: 'left' | 'right';
  $borderRadius?: string;
  $padding?: string;
  $border?: string;
  $background?: string;
  $disabled?: boolean;
  $backgroundColor?: string;
}>`
  width: 100%;
  height: ${({ $height }) => $height ?? '100%'};
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ $iconAlign }) => {
    if ($iconAlign === 'right') return 'row-reverse';
    return 'row';
  }};
  
  border-radius: ${({ $borderRadius }) => $borderRadius};
  overflow: hidden;
  border: ${({ $border }) => $border};
  background: ${({ $background }) => $background};
  background-color: ${({ $disabled, theme, $backgroundColor }) => {
    if ($disabled) return theme.color.inputGrey;
    return $backgroundColor;
  }};
`;

const InputComponent = styled.input<{
  name?: string;
  $padding: string;
  $fontSize: string;
  $color?: string;
  $disabled?: boolean;
  $textAlign?: 'left' | 'right' | 'center';
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: string;
  $placeholder?: string;
}>`
  width: 100%;
  align-self: center;
  border-color: ${({ theme }) => theme.color.transparent};
  color: ${({ $color }) => $color};
  background-color: ${({ $disabled, theme }) => {
    if ($disabled) return theme.color.transparent;
    return theme.color.transparent;
  }};
  ${({ $textAlign }) => $textAlign != null && `text-align: ${$textAlign};`}
  &::placeholder {
    color: ${({ theme }) => theme.color.mobilityGrey};
  }
  padding: ${({ $padding }) => $padding};
  font-size: ${({ $fontSize }) => $fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.weight400};
  line-height: ${({ theme }) => theme.lineHeight.height150};
`;

const WeInput = forwardRef(
  (
    {
      name,
      placeholder,
      border,
      borderRadius,
      fontSize,
      padding,
      color,
      focusColor,
      value,
      onChange,
      onKeyDown,
      focusBorder,
      defaultValue,
      disabled,
      minWidth,
      maxWidth,
      backgroundColor,
      background,
      height,
      textAlign,
    }: WeInputProps,
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = () => {
      setIsFocused(true);
    };
    const handleBlur = () => {
      setIsFocused(false);
    };

    return (
      <InputRoot
        $border={isFocused ? focusBorder : border}
        $borderRadius={borderRadius}
        $backgroundColor={backgroundColor}
        $height={height}
      >
        <InputComponent
          name={name}
          onChange={onChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          value={value}
          $placeholder={placeholder}
          $textAlign={textAlign}
          $disabled={disabled}
          $color={color}
          $fontSize={fontSize}
          $padding={padding}
        />
      </InputRoot>
    );
  }
);

WeInput.displayName = 'WeInput';

export default WeInput;
